import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { MainLayout } from '../../layouts/main/main';
import { SearchListingContainer, PageContent } from '../styles';
import { useStyles } from '../styles';
import { Grid, Button, Typography, Popover, Tab } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
    ISearchListingQuery,
    IFilterListingParams,
    ISearchListingResults,
} from '../../interfaces/search';

import { RootStoreContext } from '../../global/storeContext';
import { initializeFilterParams, initializeSearchQuery } from '../../utils/query';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { GoogleMapsApiService } from '../../global/google-maps';
import { throttle, debounce } from 'lodash';
import { Loader } from '../../components/loader/loader';
import { filterListingProperty } from '../../utils/filter';
import { ListingMap } from './ListingMap';
import { BedsBathsFilter, HomeTypeFilter, PriceFilter } from './Filters';
import { CardListing } from '../../components/uiComponents/card';
import { ToIAddress } from '../../utils/address';
import {
    LatLngLiteral,
    IdPricedListingProperty,
    GeocoderRequest,
    GeocoderResult,
} from 'realhaus-sdk';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { DEFAULT_LISTINGS_LIMIT } from '../../stores/listingStore';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useIsMobile } from '../../components/uiComponents/UIComponents';

interface FilterButtonElements {
    more: HTMLButtonElement | null;
    type: HTMLButtonElement | null;
    price: HTMLButtonElement | null;
    rooms: HTMLButtonElement | null;
    moveIn: HTMLButtonElement | null;
}
enum Tabs {
    List = 'List',
    Map = 'Map',
}

const mapService: { current?: GoogleMapsApiService | null } = {};

const DEFAULT_Q = 'calgary--ab';

export const PropertiesListPage: React.FC = observer(() => {
    const classes = useStyles();

    const navigate = useNavigate();
    const location = useLocation();

    const loaded = useRef(false);

    const { listingStore, uiStore, analyticsStore, googleMapStore } = useContext(RootStoreContext);

    const [loading, setLoading] = useState(true);

    const searchParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );

    const effectiveSearchParams = useMemo(() => {
        const params = new URLSearchParams(searchParams);
        if (!params.get('q')) params.set('q', DEFAULT_Q);
        return params;
    }, [searchParams]);

    const [searchResults, setSearchResults] = React.useState<ISearchListingResults>({});

    const [filteredSearchResults, setFilteredSearchResults] = React.useState<
        IdPricedListingProperty[]
    >([]);

    const [searchListingQuery, setSearchListingQuery] = React.useState<ISearchListingQuery>(
        initializeSearchQuery(effectiveSearchParams),
    );

    const [filterListingParams, setFilterListingParams] = React.useState<IFilterListingParams>(
        initializeFilterParams(searchParams),
    );

    const prevSearchKeyRef = useRef<string>('');

    // Mount-only: if no `q`, default to DEFAULT_Q and try geolocation refinement.
    useEffect(() => {
        const current = new URLSearchParams(window.location.search);
        if (current.has('q') && current.get('q')) return;

        current.set('q', DEFAULT_Q);
        navigate(`/search?${current.toString()}`, { replace: true });

        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const loc: LatLngLiteral = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                };
                googleMapStore.apiService?.geocode({ location: loc }, (results, status) => {
                    if (!results || results.length === 0 || status !== 'OK') return;
                    const addr = ToIAddress(results[0].address_components, results[0].geometry);
                    if (
                        addr.city &&
                        addr.province &&
                        addr.country?.toLocaleLowerCase() === 'canada'
                    ) {
                        const params = new URLSearchParams(window.location.search);
                        params.set('q', `${addr.city}--${addr.province}`);
                        navigate(`/search?${params.toString()}`, { replace: true });
                    }
                });
            },
            () => {
                // Geolocation unavailable or denied — fallback to calgary--ab.
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reconcile state from URL whenever it changes.
    useEffect(() => {
        const newQuery = initializeSearchQuery(effectiveSearchParams);
        const newFilters = initializeFilterParams(searchParams);

        const newSearchKey = JSON.stringify({
            q: searchParams.get('q'),
            lat: searchParams.get('lat'),
            lng: searchParams.get('lng'),
            dist: searchParams.get('dist'),
        });
        const searchChanged = newSearchKey !== prevSearchKeyRef.current;
        prevSearchKeyRef.current = newSearchKey;

        setSearchListingQuery(newQuery);
        setFilterListingParams(newFilters);

        if (searchChanged) {
            setSearchResults({});
            setLastQueryDoc(null);
            setLoading(true);
        }
    }, [location.search, searchParams, effectiveSearchParams]);

    const [lastQueryDoc, setLastQueryDoc] =
        React.useState<QueryDocumentSnapshot<DocumentData> | null>(null);

    const [canFetchMoreListings, setCanFetchMoreListings] = React.useState<boolean>(false);

    const [anchorEl, setAnchorEl] = React.useState<FilterButtonElements>({
        more: null,
        type: null,
        price: null,
        rooms: null,
        moveIn: null,
    });

    const handleFilterClick = (name: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl({
            ...anchorEl,
            [name]: event.currentTarget,
        });
    };

    const handleFilterClose = (name: string) => () => {
        setAnchorEl({
            ...anchorEl,
            [name]: null,
        });
    };

    const openFilters = {
        more: Boolean(anchorEl.type),
        type: Boolean(anchorEl.type),
        price: Boolean(anchorEl.price),
        rooms: Boolean(anchorEl.rooms),
        moveIn: Boolean(anchorEl.moveIn),
    };

    const filtersIds = {
        more: openFilters.more ? 'simple-popover-for-more' : undefined,
        type: openFilters.type ? 'simple-popover-for-type' : undefined,
        price: openFilters.price ? 'simple-popover-for-price' : undefined,
        rooms: openFilters.rooms ? 'simple-popover-for-rooms' : undefined,
        moveIn: openFilters.moveIn ? 'simple-popover-for-moveIn' : undefined,
    };

    const handleFilterRoomsChange = (bedrooms: string[], bathrooms: string[]) => {
        const params = new URLSearchParams(location.search);
        if (!!bedrooms && bedrooms.length > 0 && !bedrooms.includes('any')) {
            params.set('bedrooms', `${bedrooms.join(',')}`);
        } else {
            params.delete('bedrooms');
        }

        if (!!bathrooms && bathrooms.length > 0 && !bathrooms.includes('any')) {
            params.set('bathrooms', `${bathrooms.join(',')}`);
        } else {
            params.delete('bathrooms');
        }

        navigate(`/search?${params.toString()}`);
    };

    const handleFilterChange = (name: string) => (value: any) => {
        const params = new URLSearchParams(location.search);
        if (!!value && value !== 'any') {
            params.set(name, `${value}`);
        } else {
            params.delete(name);
        }
        navigate(`/search?${params.toString()}`);
    };

    const handleFilterDateChange = (date: MaterialUiPickersDate) => {
        const params = new URLSearchParams(location.search);
        if (date) {
            params.set('moveIn', `${date?.getTime()}`);
        } else {
            params.delete('moveIn');
        }
        navigate(`/search?${params.toString()}`);
    };

    const handleFilterPriceChange = (min: any, max: any) => {
        const params = new URLSearchParams(location.search);
        if (!!min && min !== '-1') {
            params.set('minPrice', `${min}`);
        } else {
            params.delete('minPrice');
        }

        if (!!max && max !== '-1') {
            params.set('maxPrice', `${max}`);
        } else {
            params.delete('maxPrice');
        }

        navigate(`/search?${params.toString()}`);
    };

    if (!loaded.current) {
        // Load Google Maps Api Services (we already have the Google API script loaded on page by google-map-react)
        mapService.current = new GoogleMapsApiService(true);
    }

    const fetchAddressGeocode = useMemo(
        () =>
            throttle(
                (
                    request: GeocoderRequest,
                    callback?: (results?: GeocoderResult[], status?: any) => void,
                ) => mapService.current?.geocode(request, callback),
                200,
            ),
        [],
    );

    const findProperties = (append: boolean = false) => {
        uiStore.showLoading();
        listingStore.searchAvailableListings(searchListingQuery, append ? lastQueryDoc : null).then(
            (
                results: {
                    lastDoc: QueryDocumentSnapshot<DocumentData>;
                } & ISearchListingResults,
            ) => {
                const newListings = results.listings ?? [];
                setCanFetchMoreListings(newListings.length >= DEFAULT_LISTINGS_LIMIT);
                setSearchResults((prev) => ({
                    location: results.location,
                    listings: append ? [...(prev.listings ?? []), ...newListings] : newListings,
                }));
                if (newListings.length > 0) {
                    setLastQueryDoc(results.lastDoc);
                }
                uiStore.hideLoading();
            },
        );
    };

    const handleViewportChange = useMemo(
        () =>
            debounce((center: LatLngLiteral, distanceInMiles: number) => {
                const params = new URLSearchParams(window.location.search);
                const newLat = center.lat.toFixed(5);
                const newLng = center.lng.toFixed(5);
                const newDist = Math.max(distanceInMiles, 0.5).toFixed(2);
                if (
                    params.get('lat') === newLat &&
                    params.get('lng') === newLng &&
                    params.get('dist') === newDist
                ) {
                    return;
                }
                params.set('lat', newLat);
                params.set('lng', newLng);
                params.set('dist', newDist);
                navigate(`/search?${params.toString()}`, { replace: true });
            }, 500),
        [navigate],
    );

    useEffect(() => {
        return () => {
            handleViewportChange.cancel();
        };
    }, [handleViewportChange]);

    useEffect(() => {
        setLoading(true);
        if (!searchListingQuery.center) {
            const { query } = searchListingQuery;
            fetchAddressGeocode(
                {
                    address: query,
                },
                (results?: GeocoderResult[]) => {
                    if (results && results.length > 0) {
                        const geolocation = results[0].geometry.location;
                        const center = {
                            lat: geolocation.lat(),
                            lng: geolocation.lng(),
                        } as LatLngLiteral;
                        const formattedAddress = results[0].formatted_address;
                        setSearchListingQuery({ query, center, formattedAddress });
                    }
                },
            );
        } else if (!searchResults.listings) {
            // Find properties with search query
            findProperties();
        }
        if (searchResults.listings) {
            // Filtering properties with search filter params
            setFilteredSearchResults(
                filterListingProperty(searchResults.listings, filterListingParams),
            );

            setLoading(false);
        }
        setLoading(false);
    }, [searchListingQuery, searchResults, filterListingParams, fetchAddressGeocode, listingStore]);

    useEffect(() => {
        const { query } = searchListingQuery;
        analyticsStore.trackSearch(query);
    }, [searchListingQuery]);

    const homeTypeFilterTitle = () =>
        !!searchParams.get('type') ? `*${searchParams.get('type')}` : 'Home Type';

    const priceRangeFilterTitle = () =>
        !!searchParams.get('minPrice') && !!searchParams.get('maxPrice')
            ? `$${searchParams.get('minPrice')} CAD - $${searchParams.get('maxPrice')} CAD`
            : !!searchParams.get('minPrice')
            ? `>= $${searchParams.get('minPrice')} CAD`
            : !!searchParams.get('maxPrice')
            ? `<= ${searchParams.get('maxPrice')}`
            : 'Price Range';

    const [tab, setTab] = React.useState(Tabs.List);

    const handleTabChange = (event: React.MouseEvent<HTMLElement>, t: Tabs) => {
        setTab(t);
    };
    const isMobile = useIsMobile();

    const mapContent = (
        <Grid item xs={12} sm={6}>
            <Grid item xs={12}>
                <SearchListingContainer>
                    {searchListingQuery.center && (
                        <ListingMap
                            listings={filteredSearchResults}
                            centerCoordinate={searchListingQuery.center}
                            onViewportChanged={handleViewportChange}
                            mapKey={searchListingQuery.query}
                        />
                    )}
                </SearchListingContainer>
            </Grid>
        </Grid>
    );
    const propertiesListContent = (
        <Grid
            container
            item
            style={{
                maxHeight: 'calc(100vh - 270px)',
                overflowY: 'scroll',
            }}
        >
            {filteredSearchResults.length === 0 ? (
                <div>
                    <Typography display='block' variant='h6' className={classes.noWrap}>
                        No Rentals where found.
                    </Typography>
                </div>
            ) : null}
            <Grid container item spacing={1} justifyContent='flex-start' alignItems='flex-start'>
                {filteredSearchResults.map((l) => (
                    <Grid item xs={6} key={`listing-card-${l.listingId}`}>
                        <CardListing
                            listingId={l.listingId}
                            key={`listing-card-${l.listingId}`}
                            isBookmarked={() => {}}
                        />
                    </Grid>
                ))}
            </Grid>
            <Grid
                container
                item
                spacing={1}
                justifyContent='center'
                alignItems='center'
                style={{ marginTop: '1rem' }}
            >
                <Button
                    color='primary'
                    onClick={() => findProperties(true)}
                    disabled={!canFetchMoreListings}
                >
                    Load more...
                </Button>
            </Grid>
        </Grid>
    );
    return (
        <>
            <Loader loading={loading} />
            <MainLayout fullWidth={true}>
                <PageContent>
                    <section>
                        <Grid container spacing={2}>
                            {/* LIST */}
                            <Grid item xs={12} sm={6}>
                                <Grid container item xs={12} spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography display='block' variant='h5'>
                                            {searchListingQuery.formattedAddress
                                                ? `Rentals in ${searchListingQuery.formattedAddress}`
                                                : 'Searching...'}
                                        </Typography>
                                    </Grid>
                                    <Grid container item spacing={1}>
                                        <Grid item>
                                            <Button
                                                aria-describedby={filtersIds.type}
                                                className={classes.noWrap}
                                                size='small'
                                                variant='outlined'
                                                color='primary'
                                                onClick={handleFilterClick('type')}
                                                endIcon={<ExpandMoreIcon />}
                                            >
                                                {homeTypeFilterTitle()}
                                            </Button>
                                            <Popover
                                                id={filtersIds.type}
                                                open={openFilters.type}
                                                anchorEl={anchorEl.type}
                                                onClose={handleFilterClose('type')}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                <HomeTypeFilter
                                                    value={searchParams.get('type') || 'any'}
                                                    onChange={(v) => {
                                                        handleFilterChange('type')(v);
                                                        handleFilterClose('type')();
                                                    }}
                                                />
                                            </Popover>
                                        </Grid>

                                        <Grid item>
                                            <Button
                                                aria-describedby={filtersIds.price}
                                                className={classes.noWrap}
                                                size='small'
                                                variant='outlined'
                                                color='primary'
                                                onClick={handleFilterClick('price')}
                                                endIcon={<ExpandMoreIcon />}
                                            >
                                                {priceRangeFilterTitle()}
                                            </Button>
                                            <Popover
                                                id={filtersIds.price}
                                                open={openFilters.price}
                                                anchorEl={anchorEl.price}
                                                onClose={handleFilterClose('price')}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                <PriceFilter
                                                    minValue={searchParams.get('minPrice')}
                                                    maxValue={searchParams.get('maxPrice')}
                                                    onChange={(min, max) => {
                                                        handleFilterPriceChange(min, max);
                                                        handleFilterClose('price')();
                                                    }}
                                                />
                                            </Popover>
                                        </Grid>

                                        <Grid item>
                                            <Button
                                                aria-describedby={filtersIds.rooms}
                                                className={classes.noWrap}
                                                size='small'
                                                variant='outlined'
                                                color='primary'
                                                onClick={handleFilterClick('rooms')}
                                                endIcon={<ExpandMoreIcon />}
                                            >
                                                Rooms
                                            </Button>
                                            <Popover
                                                id={filtersIds.rooms}
                                                open={openFilters.rooms}
                                                anchorEl={anchorEl.rooms}
                                                onClose={handleFilterClose('rooms')}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                <BedsBathsFilter
                                                    bedrooms={searchParams.get('bedrooms') ?? 'any'}
                                                    bathrooms={
                                                        searchParams.get('bathrooms') ?? 'any'
                                                    }
                                                    onChange={(beds, baths) => {
                                                        handleFilterRoomsChange(beds, baths);
                                                        handleFilterClose('rooms')();
                                                    }}
                                                />
                                            </Popover>
                                        </Grid>

                                        <Grid item>
                                            <Button
                                                aria-describedby={filtersIds.moveIn}
                                                className={classes.noWrap}
                                                size='small'
                                                variant='outlined'
                                                color='primary'
                                                onClick={handleFilterClick('moveIn')}
                                                endIcon={<ExpandMoreIcon />}
                                            >
                                                Date Available
                                            </Button>
                                            <Popover
                                                id={filtersIds.moveIn}
                                                open={openFilters.moveIn}
                                                anchorEl={anchorEl.moveIn}
                                                onClose={handleFilterClose('moveIn')}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    item
                                                    className={classes.filterFormContainer}
                                                >
                                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                        <Grid
                                                            container
                                                            justifyContent='space-around'
                                                        >
                                                            <KeyboardDatePicker
                                                                disableToolbar
                                                                variant='inline'
                                                                label='Move In Date'
                                                                autoOk
                                                                format='MMMM dd, yyyy'
                                                                id='moveIn-picker'
                                                                value={
                                                                    searchParams.get('moveIn')
                                                                        ? new Date(
                                                                              parseInt(
                                                                                  searchParams.get(
                                                                                      'moveIn',
                                                                                  ) as string,
                                                                              ),
                                                                          )
                                                                        : Date.now()
                                                                }
                                                                minDate={new Date(Date.now())}
                                                                onChange={handleFilterDateChange}
                                                                KeyboardButtonProps={{
                                                                    'aria-label': 'add date',
                                                                }}
                                                                fullWidth
                                                            />
                                                        </Grid>
                                                    </MuiPickersUtilsProvider>
                                                </Grid>
                                            </Popover>
                                        </Grid>
                                        {isMobile && (
                                            <Grid item>
                                                <ToggleButtonGroup
                                                    color='primary'
                                                    value={tab}
                                                    exclusive
                                                    onChange={handleTabChange}
                                                    aria-label='View Options'
                                                >
                                                    <ToggleButton
                                                        value={Tabs.Map}
                                                        style={{ padding: 3, fontSize: 11 }}
                                                    >
                                                        Map
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        value={Tabs.List}
                                                        style={{ padding: 3, fontSize: 11 }}
                                                    >
                                                        List
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            </Grid>
                                        )}
                                    </Grid>
                                    {!loading
                                        ? isMobile && tab === Tabs.List && propertiesListContent
                                        : ''}

                                    {!loading ? !isMobile && propertiesListContent : ''}
                                </Grid>
                            </Grid>
                            {/* MAP */}
                            {isMobile && tab === Tabs.Map && mapContent}
                            {!isMobile && mapContent}
                        </Grid>
                    </section>
                </PageContent>
            </MainLayout>
        </>
    );
});
